import React, { FC, useEffect, memo } from 'react';
import { useRouter } from 'next/router';
import { Box, Grid, Theme, useMediaQuery } from '@material-ui/core';

import { Pagination } from '@keaze/web/components/pagination';
import { CardGroup } from '@keaze/web/components/cardGroup';
import {
  SortOptions,
  Listing,
  Pagination as TypePagination,
} from '@keaze/web/common/interfaces';
import { QueryParams, OccupancyType } from '@keaze/web/common/enums';
import {
  DEFAULT_PER_PAGE,
  MAX_NUMBER_OF_ITEMS_TO_DISPLAY_MORE_RESULTS,
  useSearch,
} from './hooks';
import {
  SearchHero,
  CardWrapper,
  SearchSection,
  PropertiesFound,
  PropertiesTitle,
  PropertiesGrid,
  PersonalizedSearchWrapper,
  ItemProperties,
} from './search.styles';
import { PersonalizedSearchButton } from './personalizedSearchButton';
import { Filters } from './filters';
import { NotFound } from './notFound';
import { SortBy } from './sortBy';
import { replaceRouteQuery } from '@keaze/web/utils/replaceRouteQuery';

type Props = {
  occupancyTypeName: string;
};

type OnSortingSelector = (value: string) => void;

type OnChangeWithPersonalizedResults = (value: boolean) => void;

interface RenderPersonalizedSearchArgs {
  sortBy: string;
  sortOptions: SortOptions;
  // isPersonalizedSearchButton: boolean;
  onSortingSelector: OnSortingSelector;
  onChangeWithPersonalizedResults: OnChangeWithPersonalizedResults;
}

interface RenderCardsArgs {
  isLoading: boolean;
  cardsData: Listing[];
  withPersonalizedBanner: boolean;
}

type RenderProperties = (
  paginationTotal: number,
  occupancyTypeName: string
) => JSX.Element;

type RenderPersonalizedSearch = (
  args: RenderPersonalizedSearchArgs
) => JSX.Element;

type RenderCards = (args: RenderCardsArgs) => JSX.Element;

type RenderPropertiesGridArgs = {
  pagination: TypePagination;
  occupancyTypeName: string;
  sortBy: string;
  sortOptions: SortOptions;
  // isPersonalizedSearchButton: boolean;
  onSortingSelector: OnSortingSelector;
  onChangeWithPersonalizedResults: OnChangeWithPersonalizedResults;
};

type RenderPropertiesGrid = (args: RenderPropertiesGridArgs) => JSX.Element;

const renderProperties: RenderProperties = (
  paginationTotal,
  occupancyTypeName
) => (
  <>
    <PropertiesFound variant="caption" color="textPrimary">
      {paginationTotal} results
    </PropertiesFound>
    <PropertiesTitle variant="h1">
      Properties {occupancyTypeName}
    </PropertiesTitle>
  </>
);

const renderPersonalizedSearch: RenderPersonalizedSearch = ({
  sortBy,
  sortOptions,
  // isPersonalizedSearchButton,
  onSortingSelector,
  onChangeWithPersonalizedResults,
}) => (
  <PersonalizedSearchWrapper
    container
    alignItems="center"
    justifyContent="flex-end"
    wrap="nowrap"
  >
    {/* <PersonalizedSearchButton
      buttonActive={isPersonalizedSearchButton}
      onChange={onChangeWithPersonalizedResults}
    /> */}
    <SortBy value={sortBy} options={sortOptions} onChange={onSortingSelector} />
  </PersonalizedSearchWrapper>
);

const renderCards: RenderCards = ({
  isLoading,
  cardsData,
  withPersonalizedBanner,
}) => {
  const isData = cardsData.length > 0;
  const withMoreResults =
    isData && cardsData.length <= MAX_NUMBER_OF_ITEMS_TO_DISPLAY_MORE_RESULTS;
  const customData = isLoading ? Array(DEFAULT_PER_PAGE).fill(null) : cardsData;

  if (isLoading || isData) {
    return (
      <CardWrapper>
        <CardGroup
          data={customData}
          GridItemProps={{ xs: 12, sm: 6, md: 4, lg: 4 }}
          isLoading={isLoading}
          withFavorite
          withoutImageOptimize
          withPersonalizedBanner={withPersonalizedBanner}
        />
        {withMoreResults && <NotFound title={'Get more results'} />}
      </CardWrapper>
    );
  }

  return <NotFound />;
};

const renderPropertiesGrid: RenderPropertiesGrid = ({
  pagination,
  occupancyTypeName,
  sortBy,
  sortOptions,
  // isPersonalizedSearchButton,
  onSortingSelector,
  onChangeWithPersonalizedResults,
}) => (
  <PropertiesGrid
    container
    direction="row"
    justifyContent="space-between"
    alignContent="center"
  >
    <ItemProperties item xs={12} md={6}>
      {renderProperties(pagination.total, occupancyTypeName)}
    </ItemProperties>
    <Grid item xs={12} md={6}>
      {renderPersonalizedSearch({
        sortBy,
        sortOptions,
        // isPersonalizedSearchButton,
        onSortingSelector,
        onChangeWithPersonalizedResults,
      })}
    </Grid>
  </PropertiesGrid>
);

export const Search: FC<Props> = memo(({ occupancyTypeName }) => {
  const router = useRouter();
  const {
    // isPersonalizedSearchButton,
    isError,
    isLoading,
    withPersonalizedBanner,
    searchData: { data: cardsData, facets, sortOptions, pagination },
    sortBy,
    page,
    onSearchFiltersChange,
    onSortingSelector,
    onChangePagination,
    onChangeWithPersonalizedResults,
  } = useSearch();
  const isUpMedium = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up('md')
  );

  // Set default search type
  useEffect(() => {
    const occupancyType = router.query[QueryParams.OccupancyType];

    if (!occupancyType) {
      replaceRouteQuery(router, {
        ...router.query,
        [QueryParams.OccupancyType]: OccupancyType.Sale,
      });
    }
  }, [router]);

  if (isError) return <div>Error</div>;

  return (
    <>
      <SearchHero />
      <SearchSection>
        <Filters
          facets={facets}
          isLoading={isLoading}
          onChange={onSearchFiltersChange}
        />
        <Box>
          {renderPropertiesGrid({
            pagination,
            occupancyTypeName,
            sortBy,
            sortOptions,
            // isPersonalizedSearchButton,
            onSortingSelector,
            onChangeWithPersonalizedResults,
          })}
          {renderCards({
            isLoading,
            cardsData,
            withPersonalizedBanner,
          })}
        </Box>
        <Pagination
          count={pagination.lastPage}
          page={page}
          onChange={onChangePagination}
          siblingCount={isUpMedium ? 1 : 0}
        />
      </SearchSection>
    </>
  );
});
